import { useState } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import DraggableFlatList from "react-native-draggable-flatlist";
import { DotsSix, NotePencil, Sparkle, Trash } from "@phosphor-icons/react";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomTextInput from "../../../common/general/CustomTextInput/CustomTextInput";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import ObjectiveForm from "./ObjectiveForm";

const ObjectivesWithCriteriaList = ({
  objectivesWithCriteria = [],
  onRemoveObjectiveWithCriteria,
  onUpdateCriteria,
  onRegenerateCriteria,
  onReorderObjectives,
  onEditObjectiveWithCriteria,
  disabled = false,
  isGeneratingCriteria = false,
  isRegeneratingCriteriaForObjective = {},
}) => {
  const [editingIndex, setEditingIndex] = useState(null);
  const [editObjective, setEditObjective] = useState("");
  const [editCriteria, setEditCriteria] = useState("");

  // Ensure each objective has a unique id for the draggable list
  const objectivesWithIds = objectivesWithCriteria.map((objective, index) => {
    // Create a stable ID and ensure all properties are preserved
    return {
      ...objective,
      id: objective.id || `objective-${index}`,
    };
  });

  const handleEditStart = (item) => {
    const index = objectivesWithIds.findIndex((obj) => obj.id === item.id);
    setEditingIndex(index);
    setEditObjective(item.objective);
    setEditCriteria(item.evalCriteria && item.evalCriteria.length > 0 ? item.evalCriteria.join("\n") : "");
  };

  const handleEditCancel = () => {
    setEditingIndex(null);
    setEditObjective("");
    setEditCriteria("");
  };

  const handleEditSave = (data) => {
    const updatedObjective = {
      ...objectivesWithIds[editingIndex],
      objective: data.objective,
      evalCriteria: data.criteria ? [data.criteria] : [],
    };

    if (onEditObjectiveWithCriteria) {
      onEditObjectiveWithCriteria(editingIndex, updatedObjective);
    }

    setEditingIndex(null);
    setEditObjective("");
    setEditCriteria("");
  };

  const renderObjectiveItem = ({ item, index, drag, isActive }) => {
    // Find the actual index by matching the item id since DraggableFlatList index is unreliable
    const actualIndex = objectivesWithIds.findIndex((obj) => obj.id === item.id);

    const handleDragStart = () => {
      if (!disabled && !isGeneratingCriteria) {
        drag();
      }
    };

    const isEditing = editingIndex !== null && editingIndex === actualIndex;

    return (
      <View style={[styles.objectiveWithCriteriaItem, isActive && styles.draggingItem]}>
        {isEditing ? (
          <ObjectiveForm
            objective={editObjective}
            criteria={editCriteria}
            onObjectiveChange={setEditObjective}
            onCriteriaChange={setEditCriteria}
            onSave={handleEditSave}
            onCancel={handleEditCancel}
            disabled={disabled}
            isEditMode={true}
            saveButtonText="Save Changes"
          />
        ) : (
          <>
            <View style={styles.objectiveHeader}>
              <Pressable
                style={[
                  styles.dragHandle,
                  !disabled && !isGeneratingCriteria && styles.dragHandleActive,
                  disabled || isGeneratingCriteria ? styles.dragHandleDisabled : null,
                ]}
                onPressIn={handleDragStart}
                onLongPress={handleDragStart}
                delayLongPress={200}
                disabled={disabled || isGeneratingCriteria}
                hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
                <DotsSix size={16} color="#667085" />
              </Pressable>
              <CustomText
                text={item.objective}
                size="s"
                weight="bold"
                useTranslationText={false}
                style={styles.objectiveText}
              />
              <View style={styles.buttonContainer}>
                <CustomButton
                  styleType="transparent"
                  leftIcon={<Sparkle size={20} color="#667085" />}
                  size="s"
                  loading={isRegeneratingCriteriaForObjective[actualIndex]}
                  disabled={disabled || isGeneratingCriteria || isRegeneratingCriteriaForObjective[actualIndex]}
                  onPress={() => onRegenerateCriteria(actualIndex, item)}
                />
                <CustomButton
                  styleType="transparent"
                  leftIcon={<NotePencil size={20} color="#667085" />}
                  size="s"
                  onPress={() => handleEditStart(item)}
                  disabled={disabled || isGeneratingCriteria}
                />
                <CustomButton
                  styleType="transparent"
                  leftIcon={<Trash size={20} color="#667085" />}
                  size="s"
                  onPress={() => onRemoveObjectiveWithCriteria(actualIndex)}
                  disabled={disabled || isGeneratingCriteria}
                />
              </View>
            </View>

            <View style={styles.criteriaContainer}>
              {isGeneratingCriteria || isRegeneratingCriteriaForObjective[actualIndex] ? (
                <CustomTextInput
                  value="Loading..."
                  multiline
                  style={[styles.criteriaInput, styles.loadingInput]}
                  editable={false}
                />
              ) : item.evalCriteria && item.evalCriteria.length > 0 ? (
                item.evalCriteria.map((criteria, criteriaIndex) => (
                  <CustomTextInput
                    key={criteriaIndex}
                    value={criteria}
                    onChangeText={(text) => {
                      const updatedCriteria = [...item.evalCriteria];
                      updatedCriteria[criteriaIndex] = text;
                      onUpdateCriteria(actualIndex, updatedCriteria);
                    }}
                    placeholder="Evaluation criteria"
                    multiline
                    style={styles.criteriaInput}
                    editable={!disabled}
                  />
                ))
              ) : (
                <CustomTextInput
                  value=""
                  placeholder="No evaluation criteria yet. Generate criteria or add them manually."
                  multiline
                  style={styles.criteriaInput}
                  editable={!disabled}
                  onChangeText={(text) => {
                    const updatedCriteria = [text];
                    onUpdateCriteria(actualIndex, updatedCriteria);
                  }}
                />
              )}
            </View>
          </>
        )}
      </View>
    );
  };

  const handleDragEnd = ({ data }) => {
    // Simply update the sequence numbers and pass the data back
    const reorderedObjectives = data.map((objective, index) => ({
      ...objective,
      sequence: index + 1,
    }));

    if (onReorderObjectives) {
      // Add a small timeout to ensure transforms are reset
      setTimeout(() => {
        onReorderObjectives(reorderedObjectives);
      }, 50);
    }
  };

  return (
    <View style={styles.objectivesList}>
      <DraggableFlatList
        key={`objectives-${objectivesWithIds.length}-${JSON.stringify(objectivesWithIds.map((item) => item.id))}`}
        data={objectivesWithIds}
        onDragEnd={handleDragEnd}
        keyExtractor={(item, index) => `objective-${item.id || index}`}
        renderItem={renderObjectiveItem}
        contentContainerStyle={styles.draggableList}
        dragItemOverflow={false}
        dragHitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        activationDistance={8}
        dragEnabled={!disabled && !isGeneratingCriteria}
        extraData={objectivesWithIds.length}
        showsVerticalScrollIndicator={false}
        autoscrollThreshold={50}
        autoscrollSpeed={10}
        dragScale={1.02}
        dragActivationTreshold={8}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  objectivesList: {
    marginTop: 8,
  },
  draggableList: {
    paddingBottom: 8,
  },
  objectiveWithCriteriaItem: {
    marginBottom: 12,
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    overflow: "hidden",
  },
  draggingItem: {
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.15,
    shadowRadius: 6,
    elevation: 8,
    zIndex: 1000,
  },
  objectiveHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 8,
    paddingHorizontal: 16,
    backgroundColor: "#EAECF0",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  dragHandle: {
    padding: 8,
    opacity: 0.7,
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 4,
    marginRight: 8,
  },
  dragHandleActive: {
    opacity: 1,
    backgroundColor: "#F3F4F6",
  },
  dragHandleDisabled: {
    opacity: 0.3,
  },
  objectiveText: {
    flex: 1,
    fontSize: 14,
    fontWeight: 600,
  },
  buttonContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  criteriaContainer: {
    padding: 12,
  },
  criteriaInput: {
    backgroundColor: "#F9FAFB",
    borderRadius: 6,
    marginBottom: 8,
    minHeight: 80,
    textAlignVertical: "top",
  },
  loadingInput: {
    backgroundColor: "#F0F0F0",
    color: "#667085",
    fontStyle: "italic",
  },
});

export default ObjectivesWithCriteriaList;
